import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Badge, Button, Card, CardBody } from "reactstrap"
import { useVehicleStockJourney } from "hooks/stockHook"
import { formatReadableDate, formatSinceDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import formatMileage from "utils/formatMileage"

const TYPE_META = {
  stock_created: { icon: "bx bx-car", color: "primary" },
  quotation: { icon: "bx bx-file", color: "info" },
  pre_order: { icon: "bx bx-calendar-check", color: "warning" },
  money_receipt: { icon: "bx bx-receipt", color: "success" },
  movement: { icon: "bx bx-map", color: "secondary" },
  fuel_use: { icon: "bx bxs-gas-pump", color: "warning" },
  inventory: { icon: "bx bx-note", color: "primary" },
  spare_key: { icon: "bx bx-key", color: "secondary" },
  accessories_usage: { icon: "bx bx-gift", color: "info" },
  accessory_request: { icon: "bx bx-clipboard", color: "secondary" },
  work_order: { icon: "bx bx-wrench", color: "danger" },
  publish: { icon: "bx bx-share-alt", color: "success" },
}

const MONEY_LABELS = new Set(["Total", "Paid", "Deposit"])
const MILEAGE_LABELS = new Set(["Mileage"])

const getEntryLink = (type, entry) => {
  switch (type) {
    case "quotation":
      return `/quotation-list/${entry.identifier || entry.id}/view`
    case "pre_order":
      return `/pre-orders/${entry.id}`
    case "money_receipt":
      return `/money-receipts/${entry.id}`
    case "movement":
      return "/vehicle-movement-list"
    case "fuel_use":
      return "/fuel-usage-list"
    case "inventory":
      return `/vehicle-stock-inventory-list/${entry.id}/view`
    case "spare_key":
      return "/vehicle-spare-key-list"
    case "accessories_usage":
      return "/accessories-usage-list"
    case "accessory_request":
      return "/accessory-use-requests"
    case "work_order":
      return "/work-orders"
    default:
      return null
  }
}

const statusColor = slug => {
  if (
    [
      "approved",
      "active",
      "published",
      "completed",
      "booked",
      "fulfilled",
      "given_to_customer",
      "fully_paid",
      "overpaid",
      "paid",
    ].includes(slug)
  ) {
    return "success"
  }
  if (
    ["pending", "in_progress", "partially_paid", "in_stock"].includes(slug)
  ) {
    return "warning"
  }
  if (["cancelled", "rejected", "declined", "refunded"].includes(slug)) {
    return "danger"
  }
  return "secondary"
}

const formatStatus = value =>
  String(value ?? "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, char => char.toUpperCase())

const formatJourneyDate = value => {
  if (!value) return "-"
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
  if (isDateOnly) {
    return formatReadableDate(`${value}T12:00:00Z`, "MMM d, y")
  }
  return formatReadableDate(value, "MMM d, y h:mm a")
}

const JourneyMeta = ({ meta = {} }) => {
  const chips = Object.entries(meta)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([label, value]) => {
      const numeric = Number(value)
      const display = MILEAGE_LABELS.has(label)
        ? formatMileage(value)
        : MONEY_LABELS.has(label) && Number.isFinite(numeric)
          ? formatBDT(numeric)
          : String(value)
      return (
        <span className="journey-meta-chip" key={label}>
          {label}: {display}
        </span>
      )
    })

  return chips.length ? <div className="journey-item__meta">{chips}</div> : null
}

const StockJourneyTab = ({ stockId }) => {
  const [page, setPage] = useState(1)
  const [entries, setEntries] = useState([])
  const { data, isLoading, isFetching, refetch } = useVehicleStockJourney(
    stockId,
    page
  )

  const meta = data?.data
  const pageEntries = meta?.data ?? []
  const total = meta?.total ?? pageEntries.length
  const hasMore = Boolean(meta && meta.current_page < meta.last_page)

  useEffect(() => {
    setPage(1)
    setEntries([])
  }, [stockId])

  useEffect(() => {
    if (!meta) return

    if (page === 1) {
      setEntries(pageEntries)
      return
    }

    setEntries(prev => {
      const seen = new Set(prev.map(entry => `${entry.type}-${entry.id}`))
      return [
        ...prev,
        ...pageEntries.filter(
          entry => !seen.has(`${entry.type}-${entry.id}`)
        ),
      ]
    })
  }, [meta])

  const handleRefresh = () => {
    setPage(1)
    refetch()
  }

  const handleLoadMore = () => setPage(current => current + 1)

  const renderedEntries = useMemo(() => entries, [entries])

  return (
    <Card className="border-0 shadow-sm">
      <CardBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0 fw-semibold">Stock Journey</h5>
          <div className="d-flex align-items-center gap-3">
            {!isLoading && (
              <div className="text-muted small">Total: {total} events</div>
            )}
            <Button
              color="light"
              size="sm"
              title="Refresh journey"
              disabled={isLoading}
              onClick={handleRefresh}
            >
              <i className={isFetching ? "bx bx-sync bx-spin" : "bx bx-sync"} />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5 text-muted">
            <i className="bx bx-loader bx-spin bx-lg mb-2 d-block" />
            Loading journey...
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-5">
            <i className="bx bx-detail bx-lg text-muted mb-3"></i>
            <h5 className="text-muted">No journey records yet</h5>
            <p className="text-muted mb-0">
              Events linked to this vehicle will appear here in chronological
              order.
            </p>
          </div>
        ) : (
          <div className="stock-journey">
            {renderedEntries.map(entry => {
              const meta = TYPE_META[entry.type] ?? {
                icon: "bx bx-detail",
                color: "secondary",
              }
              const link = getEntryLink(entry.type, entry)

              return (
                <div className="journey-item" key={`${entry.type}-${entry.id}`}>
                  <div className="journey-item__rail">
                    <span className={`journey-item__dot bg-${meta.color}`}>
                      <i className={meta.icon} />
                    </span>
                  </div>

                  <div className="journey-item__body">
                    <div className="journey-item__head">
                      {link ? (
                        <Link to={link} className="journey-item__title">
                          {entry.title}
                        </Link>
                      ) : (
                        <span className="journey-item__title">
                          {entry.title}
                        </span>
                      )}
                      {entry.status ? (
                        <Badge
                          color={statusColor(entry.status_slug)}
                          className="ms-2"
                        >
                          {formatStatus(entry.status)}
                        </Badge>
                      ) : null}
                    </div>

                    {entry.subtitle ? (
                      <div className="journey-item__subtitle">
                        {entry.subtitle}
                      </div>
                    ) : null}

                    {entry.actor ? (
                      <div className="journey-item__actor">
                        <Badge color="light">
                          <i className="bx bx-user me-1" />
                          {entry.actor_role}: {entry.actor}
                        </Badge>
                      </div>
                    ) : null}

                    <div className="journey-item__date">
                      <i className="bx bx-calendar me-1" />
                      {formatJourneyDate(entry.date)}
                      <span className="text-muted">
                        {" "}
                        ({formatSinceDate(entry.date)})
                      </span>
                    </div>

                    <JourneyMeta meta={entry.meta} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {hasMore && (
          <div className="text-center mt-4">
            <Button
              color="primary"
              outline
              size="sm"
              disabled={isFetching}
              onClick={handleLoadMore}
            >
              {isFetching ? (
                <i className="bx bx-loader bx-spin me-1" />
              ) : (
                <i className="bx bx-plus me-1" />
              )}
              Load More
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default StockJourneyTab